import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_CONSULTATION_COLLECTION_NAME = "consultation";

export const useRxCollectionConsultation =
  (): RxCollection<Consultation> | null =>
    useRxCollection<Consultation>(RXDB_CONSULTATION_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<Consultation>>;
export type ConsultationQueryConstructor = UseRxData["1"];

const FREE_TEXT_DIAGNOSIS = {
  type: "object",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    name: {
      type: "string",
    },
    category: {
      type: "string",
    },
  },
  required: ["id", "name", "category"],
} as const;

const CONSULTATION_SCHEMA_LITERAL = {
  title: "consultation schema",
  version: 4,
  description: "Represents a consultation record",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    patientId: {
      type: "string",
      maxLength: 100,
    },
    meta: SYNCABLE_META,
    diseaseIds: {
      type: "array",
      items: {
        type: "string",
      },
    },
    freeTextDiagnoses: {
      type: "array",
      items: FREE_TEXT_DIAGNOSIS,
    },
    plan: {
      type: ["string", "null"],
    },
    presentingComplaint: {
      type: ["string", "null"],
    },
    symptomIds: {
      type: "array",
      items: {
        type: "string",
      },
    },
    problem: {
      type: ["string", "null"],
    },
    facilityId: {
      type: "string",
      maxLength: 100,
    },
    organisationId: {
      type: "string",
      maxLength: 100,
    },
    isImmunisation: {
      type: ["boolean", "null"],
    },
    isPerformedAtOtherClinic: {
      type: ["boolean", "null"],
    },
    backdatedTo: {
      type: ["string", "null"],
      format: "date-time",
    },
  },
  required: [
    "id",
    "patientId",
    "meta",
    "diseaseIds",
    "freeTextDiagnoses",
    "plan",
    "presentingComplaint",
    "problem",
    "facilityId",
    "organisationId",
    "isImmunisation",
    "isPerformedAtOtherClinic",
    "backdatedTo",
  ],
  // https://elephant.slab.com/posts/offline-schema-field-encryption-policy-uwb0pbii#hy004-consultation
  encrypted: ["problem", "plan", "presentingComplaint", "freeTextDiagnoses"],
  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(CONSULTATION_SCHEMA_LITERAL);

// aggregate the document type from the schema
export type Consultation = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

// create the typed RxJsonSchema from the literal typed object.
export const CONSULTATION_SCHEMA: RxJsonSchema<Consultation> =
  CONSULTATION_SCHEMA_LITERAL;
